import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React from 'react';

export interface MfaChallengeFormProps {
  code: string;
  isLoading: boolean;
  /** SMS_MFA challenge (code texted) rather than an authenticator app. */
  isSms?: boolean;
  onCancel: () => void;
  onCodeChange: (code: string) => void;
  onSubmit: (e: React.FormEvent) => void;
}

export const MFA_CODE_LENGTH = 6;

/**
 * HA-440: second sign-in step for accounts with an authenticator app. Shown in
 * place of the email/password form when Cognito answers the password with a
 * SOFTWARE_TOKEN_MFA challenge.
 */
const MfaChallengeForm: React.FC<MfaChallengeFormProps> = ({
  code,
  isLoading,
  isSms = false,
  onCancel,
  onCodeChange,
  onSubmit,
}) => (
  <Box component="form" onSubmit={onSubmit} noValidate data-testid="mfa-challenge-form">
    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
      {isSms
        ? 'Enter the 6-digit code we texted to your phone.'
        : 'Enter the 6-digit code from your authenticator app.'}
    </Typography>
    <TextField
      autoFocus
      fullWidth
      required
      label="Authentication code"
      value={code}
      onChange={(e) => onCodeChange(e.target.value.replace(/\D/g, '').slice(0, MFA_CODE_LENGTH))}
      slotProps={{
        htmlInput: {
          inputMode: 'numeric',
          autoComplete: 'one-time-code',
          maxLength: MFA_CODE_LENGTH,
          'data-testid': 'mfa-code-input',
        },
      }}
    />
    <Button
      type="submit"
      fullWidth
      variant="contained"
      sx={{ mt: 3, mb: 2 }}
      disabled={isLoading || code.length !== MFA_CODE_LENGTH}
      data-testid="mfa-submit-button"
    >
      {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
    </Button>
    <Link
      component="button"
      type="button"
      variant="body2"
      onClick={onCancel}
      data-testid="mfa-cancel-link"
    >
      Back to sign in
    </Link>
  </Box>
);

export default MfaChallengeForm;
